import React, { useContext, useEffect, useRef, useState } from "react";
import { navigateToUrl } from "single-spa";
import { Button, Flex, Heading, MLink, Text } from "@cisco/spaces-dash-lib-styleguide";
import { Login, CustomerSelection, logout } from "@cisco/spaces-dash-lib-login";
import moment from "moment";
import { I18nContext } from "../../contexts/i18nContext";
import { CustomerItem } from "../../types/components";
import PartnerCoverImage from "../../assets/images/partner_cover.png";
import { LoginModal } from "../modals/login/login-modal";
import { PropertiesContext } from "../../contexts/propertiesContext";
import { checkPartnerApp, checkPartnerLicenseAgreed, fetchAccounts } from "../../services/partner-login-service";
import { AdminViewSelectionModal } from "./admin-view-selection-modal";
import { SupportSelection } from "../modals/login/support-selection";
import { ContactUs } from "../modals/contact-us";
import { PartnerUnavailableModal } from "../modals/login/partner-unavailable";
import { LicenseAgreementModal } from "../modals/login/license-agreement";
import { PartnerUnauthorizedModal } from "../modals/login/partner-unauthorized";

interface LoginPageTopHeadingProps {
  isPartnerAdmin: boolean;
}

export function LoginPageTopHeading({ isPartnerAdmin }: LoginPageTopHeadingProps): JSX.Element {
  const i18n = useContext(I18nContext);
  const propertiesContext = useContext(PropertiesContext);

  if (!i18n || !propertiesContext) {
    throw new Error("I18nContext or PropertiesContext is not available");
  }

  const { t } = i18n;
  const { properties } = propertiesContext;

  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [isLoginContinue, setIsLoginContinue] = useState(false);
  const [isSupportContinue, setIsSupportContinue] = useState(false);
  const [supportCustomerList, setsupportCustomerList] = useState<CustomerItem[]>([]);
  const [isPartnerUnauthorized, setIsPartnerUnauthorized] = useState(false);
  const [isPartnerUnavailable, setIsPartnerUnavailable] = useState(false);
  const [showLicenseAgreement, setShowLicenseAgreement] = useState(false);
  const [showAdminViewSelectionPopup, setShowAdminViewSelectionPopup] = useState(false);
  const [showContactUs, setShowContactUs] = useState(false);
  const [pendingLoginSuccess, setPendingLoginSuccess] = useState(false);
  const redirectUrl = useRef<string | null>(null);
  const appId = useRef<string | null>(null);

  const getAccountStatus = (tenant: any): string => {
    const now = moment();
    if (tenant.licenses != null && !tenant.licenses.isEmpty && tenant.status === "Accepted") {
      const licenseItem = tenant.licenses[0];
      const startdate = licenseItem.startDate;
      const enddate = licenseItem.endDate;
      if (startdate != null && enddate != null) {
        try {
          const customerEndDate = moment(enddate);
          if (customerEndDate != null && customerEndDate >= now) {
            return "ACTIVE";
          }
          return "EXPIRED";
        } catch (e) {
          console.error("error while checking license", e);
          return "ACTIVE";
        }
      }
    } else if (tenant.licenses == null || tenant.licenses.isEmpty) {
      return "EXPIRED";
    } else if (tenant.status === "Pending") {
      return "INACTIVE";
    } else if (tenant.status === "Deleted") {
      return "DELETED";
    }
    return tenant.status.toUpperCase();
  };

  const getLicenseDetails = (licenses: any[]): any => {
    if (!licenses || licenses.length === 0) return {};
    const licenseItem = licenses[0];
    const startDate = moment(licenseItem.startDate);
    const endDate = moment(licenseItem.endDate);
    const currentDate = moment();
    const isExpired = currentDate.isAfter(endDate);
    return {
      startDate: licenseItem.startDate,
      endDate: licenseItem.endDate,
      termMonths: endDate.diff(startDate, "months"),
      status: isExpired ? "Expired" : "Active"
    };
  };

  const formatCustomerList = (accountDetails: any[]): CustomerItem[] => {
    const seenTenantIds = new Set();
    return accountDetails.reduce((acc: CustomerItem[], tenant: any) => {
      if (!seenTenantIds.has(tenant.tenantId)) {
        seenTenantIds.add(tenant.tenantId);
        const customerJson: CustomerItem = {
          tenantId: tenant.tenantId,
          customerName: tenant.accountName,
          accountNumber: tenant.accountNumber,
          accountStatus: getAccountStatus(tenant),
          ...getLicenseDetails(tenant.licenses)
        };
        acc.push(customerJson);
      }
      return acc;
    }, []);
  };

  const getSupportCustomerList = (authResponse: any) => {
    const formattedCustomerList = formatCustomerList(authResponse.accountDetails);
    setsupportCustomerList(formattedCustomerList);
  };

  const checkSupportAccount = async () => {
    try {
      const res = await fetchAccounts();
      if (res && (res as any).userDetails && (res as any).userDetails.userName) {
        sessionStorage.setItem("supportUserName", (res as any).userDetails.userName);
        getSupportCustomerList(res);
      } else {
        throw new Error("Invalid response");
      }
    } catch (error) {
      console.error(error);
      setIsLoginModalOpen(false);
      navigateToUrl("/manage/support/login");
    }
  };

  useEffect(() => {
    if (window.location.pathname.indexOf("login/continue") > -1) {
      setIsLoginContinue(true);
      setIsLoginModalOpen(true);
    } else if (window.location.pathname.indexOf("login/supportcontinue") > -1) {
      setIsLoginContinue(false);
      setIsLoginModalOpen(true);
      setIsSupportContinue(true);
      checkSupportAccount();
    } else if (window.location.pathname.indexOf("login/error") > -1) {
      setIsLoginModalOpen(false);
    }
    const urlParams = new URLSearchParams(window.location.search);
    redirectUrl.current = urlParams.get("redirectUrl");
    appId.current = urlParams.get("appId");
  }, []);

  // Handle pending login once properties are loaded
  useEffect(() => {
    if (pendingLoginSuccess && properties?.partnerDomainSetup) {
      const tenantId = window.sessionStorage.getItem("tenantId");
      partnerCheck(tenantId, properties.partnerDomainSetup);
      setPendingLoginSuccess(false);
    }
  }, [properties?.partnerDomainSetup, pendingLoginSuccess]);

  useEffect(() => {
    let redirectTimer: NodeJS.Timeout | undefined;

    if (supportCustomerList.length > 0) {
      redirectTimer = setTimeout(() => {
        setIsSupportContinue(false);
        navigateToUrl("/login");
      }, 120000);
    }

    return () => {
      if (redirectTimer) {
        clearTimeout(redirectTimer);
      }
    };
  }, [supportCustomerList]);

  const openLoginModal = () => {
    setIsLoginContinue(false);
    setIsLoginModalOpen(true);
  };

  const closeLoginModal = () => {
    logout();
    if (isLoginContinue) {
      navigateToUrl("/login");
    }
    setIsLoginModalOpen(false);
  };

  const checkSupportMail = (email: string | null): boolean => {
    return email?.includes("support@") ?? false;
  };

  const takeToHome = () => {
    let redirectFullUri = "/";
    if (redirectUrl.current && appId.current) {
      redirectFullUri += `/${appId.current}`;
    }
    navigateToUrl(redirectFullUri);
  };

  const showAdminViewSelection = () => {
    const partnerAdmins = properties?.partnerAdminUsers || [];
    if (properties?.partnerDomainSetup === "us" && partnerAdmins.indexOf(sessionStorage.getItem("userName") ?? "") > -1) {
      setShowAdminViewSelectionPopup(true);
    } else {
      takeToHome();
    }
  };

  const scrollToRegister = () => {
    const element = document.getElementById("partner_withus");
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  const showContactUsPopup = () => {
    setShowContactUs(true);
  };

  const licenseAgreedCheck = async () => {
    try {
      if (checkSupportMail(sessionStorage.getItem("userName"))) {
        showAdminViewSelection();
        return;
      }

      // Ensure properties are loaded before proceeding
      if (!properties?.partnerDomainSetup) {
        console.warn("Properties not loaded yet in licenseAgreedCheck");
        return;
      }

      const licenseVersion = properties.partnerDashboardLicenseVersion || "v1";
      const setup = properties.partnerDomainSetup;
      const res = await checkPartnerLicenseAgreed(sessionStorage.getItem("tenantId") ?? "", licenseVersion, setup);
      if (res && res.status) {
        if (res.data && res.data.status) {
          showAdminViewSelection();
        } else {
          setIsLoginModalOpen(false);
          setShowLicenseAgreement(true);
        }
      }
    } catch (error) {
      console.error(error);
    }
  };

  const partnerCheck = async (tenantId: string | null, partnerDomainSetup: string) => {
    try {
      if (!tenantId) {
        throw new Error("TenantId or is missing");
      }
      const res = await checkPartnerApp(tenantId, partnerDomainSetup);
      if (!res || !res.status || !res.data?.status) {
        if (!properties) {
          setIsPartnerUnavailable(true);
          setIsLoginModalOpen(false);
          return;
        }
        if (properties.partnerDomainSetup !== "eu" && properties.partnerDomainSetup !== "sg") {
          setIsPartnerUnavailable(true);
          setIsLoginModalOpen(false);
        } else {
          setIsPartnerUnauthorized(true);
        }
      } else {
        if (res.data.isInternal) {
          const isInternal = typeof res.data.isInternal === "string" ? JSON.parse(res.data.isInternal) : res.data.isInternal;
          window.sessionStorage.setItem("isInternal", String(isInternal));
        } else {
          window.sessionStorage.setItem("isInternal", "false");
        }
        licenseAgreedCheck();
      }
    } catch (error) {
      console.error(error);
      setIsPartnerUnauthorized(true);
    }
  };

  const onLoginSuccess = () => {
    const tenantId = window.sessionStorage.getItem("tenantId");
    const partnerDomainSetup = properties?.partnerDomainSetup;

    // Wait for properties to load before proceeding
    if (!partnerDomainSetup) {
      console.warn("Properties not loaded yet, waiting for partnerDomainSetup");
      setPendingLoginSuccess(true);
      return;
    }

    partnerCheck(tenantId, partnerDomainSetup);
  };

  const takeToAdminApps = () => {
    let redirectFullUri = "/admin/apps";
    if (redirectUrl.current && appId.current) {
      redirectFullUri += `/${appId.current}`;
    }
    navigateToUrl(redirectFullUri);
  };

  const adminLoginText = t("auth.login.login-partner-base.admin-login-text", { ns: "spaces-dash-app-partners-login" });
  const loginText = t("auth.activate-appuser.login-text", { ns: "spaces-dash-app-partners-login" });

  return (
    <Flex
      direction="vertical"
      style={{
        background: `url(${PartnerCoverImage}) no-repeat top left`,
        backgroundSize: "cover",
        marginTop: "-56px"
      }}
      justify="center"
      align="center"
    >
      <LoginModal isOpen={isLoginModalOpen} closeModal={closeLoginModal}>
        {!isLoginContinue && !isSupportContinue && <Login i18n={i18n} />}
        {isLoginContinue && !isSupportContinue && <CustomerSelection i18n={i18n} loginSuccess={onLoginSuccess} />}
        {!isLoginContinue && isSupportContinue && <SupportSelection supportCustomerList={supportCustomerList} onLoginSuccess={onLoginSuccess} />}
      </LoginModal>
      {showContactUs && <ContactUs isOpen={showContactUs} closeModal={() => setShowContactUs(false)} />}
      {isPartnerUnavailable && (
        <PartnerUnavailableModal
          isOpen={isPartnerUnavailable}
          closeModal={() => {
            setIsPartnerUnavailable(false);
          }}
          licenseAgreedCheck={() => void licenseAgreedCheck()}
        />
      )}
      {showLicenseAgreement && (
        <LicenseAgreementModal
          isOpen={showLicenseAgreement}
          closeModal={() => {
            setShowLicenseAgreement(false);
          }}
          showAdminViewSelection={showAdminViewSelection}
        />
      )}
      {isPartnerUnauthorized && (
        <PartnerUnauthorizedModal
          isOpen={isPartnerUnauthorized}
          closeModal={() => {
            setIsPartnerUnauthorized(false);
            closeLoginModal();
          }}
        />
      )}
      {showAdminViewSelectionPopup && <AdminViewSelectionModal takeToAdminApps={takeToAdminApps} takeToHome={takeToHome} />}
      <Flex style={{ margin: "20rem 0 6rem 0" }}>
        <Heading size="primary" style={{ fontSize: "4rem" }}>
          {t("auth.login.login-partner-base.be-part-of-cisco-spaces-text", { ns: "spaces-dash-app-partners-login" })}
        </Heading>
      </Flex>
      <Flex direction="vertical" justify="center" align="center" gap={24} style={{ marginBottom: "20rem" }}>
        <Button.Group>
          {!isPartnerAdmin && (window as any).hbspt && (
            <Button kind="secondary" onClick={scrollToRegister}>
              {t("auth.login.login-partner-base.partner-with-us-text", { ns: "spaces-dash-app-partners-login" })}
            </Button>
          )}

          <Button onClick={openLoginModal}>{isPartnerAdmin ? adminLoginText : loginText}</Button>
        </Button.Group>
        {!isPartnerAdmin && (
          <Text>
            {t("auth.login.login-partner-base.having-trouble-text", { ns: "spaces-dash-app-partners-login" })}?{" "}
            <MLink onClick={showContactUsPopup}>{t("auth.login.login-screen-six.contact-us-text", { ns: "spaces-dash-app-partners-login" })}</MLink>
          </Text>
        )}
      </Flex>
    </Flex>
  );
}
